import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Tabs, Tab, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Avatar, Button, IconButton, Tooltip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@mui/material';
import {
  CalendarToday, AccessTime, Work, People, Download, Search,
  Edit as EditIcon, Delete as DeleteIcon, Person, Schedule,
  CheckCircle, Cancel
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import CompOffApprovals from './CompOffApprovals';
import EmployeeOverviewTab from './EmployeeOverviewTab';
import ContentApprovalTab from './ContentApprovalTab';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

function fmtDate(date) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function statusColor(s) {
  if (!s) return 'default';
  switch (s.toLowerCase()) {
    case 'approved': return 'success';
    case 'pending': return 'warning';
    case 'rejected': return 'error';
    default: return 'default';
  }
}

function LeavesTable({ leaves, onEditLeave, onDeleteLeave, onApproveLeave, onRejectLeave, userRole }) {
  if (!leaves.length) return <Alert severity="info">No leave requests found for the selected period.</Alert>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell>Employee</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Leave Type</TableCell>
            <TableCell>Start Date</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>End Date</TableCell>
            <TableCell>Days</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Applied By</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Approved By</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {leaves.map(leave => (
            <TableRow key={leave.id} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                    {leave.employee?.fullName?.charAt(0) || '?'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {leave.employee?.fullName || 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{leave.leaveType}</TableCell>
              <TableCell>{fmtDate(leave.startDate)}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmtDate(leave.endDate)}</TableCell>
              <TableCell><Chip label={leave.numberOfDays} size="small" color="primary" /></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {leave.appliedByName || leave.appliedByUser?.fullName || '-'}
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {leave.approver?.fullName || (leave.status === 'pending' ? '—' : '-')}
              </TableCell>
              <TableCell><Chip label={leave.status} size="small" color={statusColor(leave.status)} /></TableCell>
              <TableCell align="center">
                <Box display="flex" gap={0.5} justifyContent="center">
                  {leave.status === 'pending' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => onApproveLeave(leave.id, leave.employee?.fullName)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => onRejectLeave(leave.id, leave.employee?.fullName)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => onEditLeave(leave)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {['owner', 'manager', 'hr', 'lead'].includes(userRole) && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => onDeleteLeave(leave.id, leave.employee?.fullName)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PermissionsTable({ permissions, onEditPermission, onDeletePermission, onApprovePermission, onRejectPermission, userRole }) {
  if (!permissions.length) return <Alert severity="info">No permission requests found for the selected period.</Alert>;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell>Employee</TableCell>
            <TableCell>Date</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Time</TableCell>
            <TableCell>Hours</TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Applied By</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {permissions.map(perm => (
            <TableRow key={perm.id} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'info.main', fontSize: '0.75rem' }}>
                    {perm.employee?.fullName?.charAt(0) || '?'}
                  </Avatar>
                  <Typography variant="body2" fontWeight={600} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {perm.employee?.fullName || 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>{fmtDate(perm.permissionDate)}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{perm.startTime} – {perm.endTime}</TableCell>
              <TableCell><Chip label={`${perm.hours}h`} size="small" color="info" /></TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                {perm.appliedByName || perm.appliedByUser?.fullName || '-'}
              </TableCell>
              <TableCell><Chip label={perm.status} size="small" color={statusColor(perm.status)} /></TableCell>
              <TableCell align="center">
                <Box display="flex" gap={0.5} justifyContent="center">
                  {perm.status === 'pending' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton size="small" color="success" onClick={() => onApprovePermission(perm.id, perm.employee?.fullName)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton size="small" color="error" onClick={() => onRejectPermission(perm.id, perm.employee?.fullName)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => onEditPermission(perm)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {['owner', 'manager', 'hr', 'lead'].includes(userRole) && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => onDeletePermission(perm.id, perm.employee?.fullName)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ManagerDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [filterType, setFilterType] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [employees, setEmployees] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [dailyWork, setDailyWork] = useState([]);
  const [contents, setContents] = useState([]);
  const [stats, setStats] = useState({ totalLeaves: 0, totalPermissions: 0, totalWorkLogs: 0, activeEmployees: 0 });

  const [overviewEmployeeId, setOverviewEmployeeId] = useState('');
  const [overviewCompOff, setOverviewCompOff] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [editLeaveDialog, setEditLeaveDialog] = useState(false);
  const [editPermissionDialog, setEditPermissionDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [leaveTypes, setLeaveTypes] = useState(['Unpaid Leave']);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, employeeName: '' });
  const [leaveFormData, setLeaveFormData] = useState({ leaveType: '', startDate: '', endDate: '', numberOfDays: '', reason: '' });
  const [permissionFormData, setPermissionFormData] = useState({ permissionDate: '', startTime: '', endTime: '', hours: '', reason: '' });

  // Manual Punch state
  const [manualPunches, setManualPunches] = useState([]);
  const [manualPunchLoading, setManualPunchLoading] = useState(false);
  const [manualPunchDialog, setManualPunchDialog] = useState({ open: false, punch: null });
  const [manualPunchForm, setManualPunchForm] = useState({ punchDate: '', inTime: '09:30', outTime: '18:30', hours: '9.00', reason: '' });
  const [manualPunchFormError, setManualPunchFormError] = useState('');
  const [manualPunchSubmitting, setManualPunchSubmitting] = useState(false);
  const [mpDeleteDialog, setMpDeleteDialog] = useState({ open: false, id: null, employeeName: '' });

  // Apply on behalf of employee
  const [mpApplyDialog, setMpApplyDialog] = useState(false);
  const [mpApplyForm, setMpApplyForm] = useState({ userId: '', punchDate: new Date().toISOString().split('T')[0], inTime: '09:30', outTime: '18:30', hours: '9.00', reason: '' });
  const [mpApplyError, setMpApplyError] = useState('');
  const [mpApplySubmitting, setMpApplySubmitting] = useState(false);

  useEffect(() => { fetchAllData(); fetchLeaveTypes(); fetchManualPunches(); }, [selectedMonth, filterType, selectedYear, dateRangeStart, dateRangeEnd]);
  useEffect(() => {
    if (overviewEmployeeId) fetchOverviewCompOff(overviewEmployeeId);
    else setOverviewCompOff([]);
  }, [overviewEmployeeId, filterType, selectedMonth, selectedYear, dateRangeStart, dateRangeEnd]);

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
  function generateMonthOptions() {
    const options = [], now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      options.push({ value, label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), isCurrent: i === 0 });
    }
    return options;
  }
  function generateYearOptions() {
    const y = new Date().getFullYear();
    return [{ value: String(y), label: `${y} (Current)` }, { value: String(y - 1), label: `${y - 1} (Previous)` }];
  }

  const fetchLeaveTypes = async () => {
    try {
      const r = await axios.get(`${API_URL}/dropdowns`);
      setLeaveTypes((r.data.data.leaveTypes || ['Unpaid Leave']).filter(t => t !== 'Comp Off'));
    } catch (e) { console.error(e); }
  };

  const fetchAllData = async () => {
    if (filterType === 'daterange' && (!dateRangeStart || !dateRangeEnd)) return;
    setLoading(true);
    try {
      let params;
      if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
        params = { filterType: 'daterange', startDate: dateRangeStart, endDate: dateRangeEnd };
      } else if (filterType === 'yearly') {
        params = { year: selectedYear, filterType: 'yearly' };
      } else {
        const [y, m] = selectedMonth.split('-');
        params = { year: y, month: m };
      }
      const response = await axios.get(`${API_URL}/employee/manager-dashboard`, { params });
      const { leaves: l, permissions: p, dailyWork: dw, contents: ct, stats: s } = response.data.data;
      setLeaves(l || []);
      setPermissions(p || []);
      setDailyWork(dw || []);
      setContents(ct || []);
      setStats(s || { totalLeaves: 0, totalPermissions: 0, totalWorkLogs: 0, activeEmployees: 0 });
      const empRes = await axios.get(`${API_URL}/users`, { params: { isActive: 'true' } });
      setEmployees(empRes.data.data?.users || []);
    } catch (e) {
      console.error(e);
      showSnackbar('Failed to fetch dashboard data', 'error');
    } finally { setLoading(false); }
  };

  const fetchManualPunches = async () => {
    setManualPunchLoading(true);
    try {
      const res = await axios.get(`${API_URL}/employee/manual-punch`);
      setManualPunches(res.data.data.punches || []);
    } catch (e) { console.error(e); }
    finally { setManualPunchLoading(false); }
  };

  const calcMpHours = (inTime, outTime) => {
    if (!inTime || !outTime) return '';
    const [ih, im] = inTime.split(':').map(Number);
    const [oh, om] = outTime.split(':').map(Number);
    if (isNaN(ih) || isNaN(im) || isNaN(oh) || isNaN(om)) return '';
    const mins = (oh * 60 + om) - (ih * 60 + im);
    return mins > 0 ? (mins / 60).toFixed(2) : '';
  };

  const openMpEditDialog = (punch) => {
    setManualPunchForm({
      punchDate: punch.punchDate,
      inTime: punch.inTime,
      outTime: punch.outTime || '',
      hours: punch.hours || '',
      reason: punch.reason
    });
    setManualPunchFormError('');
    setManualPunchDialog({ open: true, punch });
  };

  const handleMpSave = async () => {
    const { punchDate, inTime, outTime, reason } = manualPunchForm;
    if (!punchDate || !inTime || !reason.trim()) {
      setManualPunchFormError('Date, In Time and Reason are required.');
      return;
    }
    if (outTime && outTime <= inTime) {
      setManualPunchFormError('Out Time must be after In Time.');
      return;
    }
    setManualPunchSubmitting(true);
    setManualPunchFormError('');
    try {
      const hours = calcMpHours(manualPunchForm.inTime, manualPunchForm.outTime);
      await axios.put(`${API_URL}/employee/manual-punch/${manualPunchDialog.punch.id}`, {
        ...manualPunchForm, hours: hours || null, editorRole: user?.role
      });
      showSnackbar('Manual punch updated successfully', 'success');
      setManualPunchDialog({ open: false, punch: null });
      fetchManualPunches();
    } catch (e) {
      setManualPunchFormError(e.response?.data?.message || 'Error saving');
    } finally { setManualPunchSubmitting(false); }
  };

  const handleMpStatusUpdate = async (id, status, employeeName) => {
    if (!window.confirm(`${status === 'approved' ? 'Approve' : 'Reject'} manual punch for ${employeeName}?`)) return;
    try {
      await axios.put(`${API_URL}/employee/manual-punch/${id}/status`, { status, approvedBy: user?.id });
      showSnackbar(`Manual punch ${status}`, 'success');
      fetchManualPunches();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Error', 'error'); }
  };

  const confirmMpDelete = async () => {
    try {
      await axios.delete(`${API_URL}/employee/manual-punch/${mpDeleteDialog.id}`, {
        data: { deleterRole: user?.role }
      });
      showSnackbar('Manual punch request deleted', 'success');
      setMpDeleteDialog({ open: false, id: null, employeeName: '' });
      fetchManualPunches();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Error deleting', 'error'); }
  };

  const handleMpApplySubmit = async () => {
    const { userId, punchDate, inTime, outTime, reason } = mpApplyForm;
    if (!userId) { setMpApplyError('Please select an employee.'); return; }
    if (!punchDate || !inTime || !reason.trim()) { setMpApplyError('Date, In Time and Reason are required.'); return; }
    if (outTime && outTime <= inTime) { setMpApplyError('Out Time must be after In Time.'); return; }
    setMpApplySubmitting(true);
    setMpApplyError('');
    try {
      const hours = calcMpHours(inTime, outTime);
      await axios.post(`${API_URL}/employee/manual-punch`, {
        userId,
        punchDate,
        inTime,
        outTime: outTime || null,
        hours: hours || null,
        reason,
        appliedBy: user?.id
      });
      showSnackbar('Manual punch request submitted for employee', 'success');
      setMpApplyDialog(false);
      setMpApplyForm({ userId: '', punchDate: new Date().toISOString().split('T')[0], inTime: '09:30', outTime: '18:30', hours: '9.00', reason: '' });
      fetchManualPunches();
    } catch (e) {
      setMpApplyError(e.response?.data?.message || 'Error submitting request');
    } finally { setMpApplySubmitting(false); }
  };

  const fetchOverviewCompOff = async (empId) => {
    setOverviewLoading(true);
    try {
      let params = { userId: empId };
      if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
        params.filterType = 'daterange';
        params.startDate = dateRangeStart;
        params.endDate = dateRangeEnd;
      } else if (filterType === 'yearly') {
        params.filterType = 'yearly';
        params.year = selectedYear;
      } else {
        const [y, m] = selectedMonth.split('-');
        params.year = y;
        params.month = m;
      }
      const r = await axios.get(`${API_URL}/comp-off/all`, { params });
      setOverviewCompOff(r.data.data?.claims || []);
    } catch (e) { setOverviewCompOff([]); }
    finally { setOverviewLoading(false); }
  };

  const handleEditLeave = (leave) => {
    setSelectedLeave(leave);
    setLeaveFormData({ leaveType: leave.leaveType, startDate: new Date(leave.startDate).toISOString().split('T')[0], endDate: new Date(leave.endDate).toISOString().split('T')[0], numberOfDays: leave.numberOfDays, reason: leave.reason });
    setEditLeaveDialog(true);
  };
  const handleEditPermission = (perm) => {
    setSelectedPermission(perm);
    setPermissionFormData({ permissionDate: new Date(perm.permissionDate).toISOString().split('T')[0], startTime: perm.startTime, endTime: perm.endTime, hours: perm.hours, reason: perm.reason });
    setEditPermissionDialog(true);
  };
  const handleSaveLeave = async () => {
    try { await axios.put(`${API_URL}/employee/leaves/${selectedLeave.id}`, leaveFormData); showSnackbar('Leave updated'); setEditLeaveDialog(false); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleSavePermission = async () => {
    try { await axios.put(`${API_URL}/employee/permissions/${selectedPermission.id}`, permissionFormData); showSnackbar('Permission updated'); setEditPermissionDialog(false); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleDeleteLeave = async () => {
    try { await axios.delete(`${API_URL}/employee/leaves/${deleteDialog.id}`); showSnackbar('Leave deleted'); setDeleteDialog({ open: false, type: null, id: null, employeeName: '' }); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleDeletePermission = async () => {
    try { await axios.delete(`${API_URL}/employee/permissions/${deleteDialog.id}`); showSnackbar('Permission deleted'); setDeleteDialog({ open: false, type: null, id: null, employeeName: '' }); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleDeleteWork = async () => {
    try { await axios.delete(`${API_URL}/employee/daily-work/${deleteDialog.id}`); showSnackbar('Work log deleted'); setDeleteDialog({ open: false, type: null, id: null, employeeName: '' }); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleApproveLeave = async (id, name) => {
    try { await axios.put(`${API_URL}/employee/leaves/${id}/status`, { status: 'approved', approvedBy: user?.id }); showSnackbar(`Leave approved for ${name}`); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleRejectLeave = async (id, name) => {
    try { await axios.put(`${API_URL}/employee/leaves/${id}/status`, { status: 'rejected', approvedBy: user?.id }); showSnackbar(`Leave rejected for ${name}`, 'warning'); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleApprovePermission = async (id, name) => {
    try { await axios.put(`${API_URL}/employee/permissions/${id}/status`, { status: 'approved', approvedBy: user?.id }); showSnackbar(`Permission approved for ${name}`); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const handleRejectPermission = async (id, name) => {
    try { await axios.put(`${API_URL}/employee/permissions/${id}/status`, { status: 'rejected', approvedBy: user?.id }); showSnackbar(`Permission rejected for ${name}`, 'warning'); fetchAllData(); }
    catch (e) { showSnackbar(e.response?.data?.message || 'Failed', 'error'); }
  };
  const confirmDelete = () => {
    if (deleteDialog.type === 'leave') handleDeleteLeave();
    else if (deleteDialog.type === 'permission') handleDeletePermission();
    else if (deleteDialog.type === 'work') handleDeleteWork();
  };
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const exportToExcel = () => {
    let headers = [], data = [], filename = '';
    if (activeTab === 0) {
      headers = ['Employee', 'Leave Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason'];
      data = filteredLeaves.map(l => [l.employee?.fullName || '-', l.leaveType, fmtDate(l.startDate), fmtDate(l.endDate), l.numberOfDays, l.status, l.reason]);
      filename = `Leaves_${selectedMonth}.csv`;
    } else if (activeTab === 1) {
      headers = ['Employee', 'Date', 'Start Time', 'End Time', 'Hours', 'Status', 'Reason'];
      data = filteredPermissions.map(p => [p.employee?.fullName || '-', fmtDate(p.permissionDate), p.startTime, p.endTime, p.hours, p.status, p.reason]);
      filename = `Permissions_${selectedMonth}.csv`;
    }
    if (!filename) return;
    const csv = [headers.join(','), ...data.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = filename; a.click();
  };

  const filteredLeaves = leaves.filter(l => !searchQuery || l.employee?.fullName?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredPermissions = permissions.filter(p => !searchQuery || p.employee?.fullName?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredTotalDays = filteredLeaves.reduce((s, l) => s + (parseFloat(l.numberOfDays) || 0), 0);
  const filteredTotalHours = filteredPermissions.reduce((s, p) => s + (parseFloat(p.hours) || 0), 0);

  // Employee Overview computed
  const selectedEmployee = employees.find(e => e.id === overviewEmployeeId);
  const empLeaves = leaves.filter(l => l.userId === overviewEmployeeId || l.employee?.id === overviewEmployeeId);
  const empPermissions = permissions.filter(p => p.userId === overviewEmployeeId || p.employee?.id === overviewEmployeeId);
  const empDailyWork = dailyWork.filter(w => w.userId === overviewEmployeeId || w.employee?.id === overviewEmployeeId);

  // Also include Content Library entries where this employee appears in any role
  const empName = selectedEmployee?.fullName?.trim().toLowerCase();
  const CONTENT_ROLE_FIELDS = ['director', 'editor', 'cinematographer', 'writer'];

  // Name matching: handles "Sridhar" matching "Sridhar Rajagopal" and vice versa
  const nameMatches = (fieldVal, fullName) => {
    if (!fieldVal || !fullName) return false;
    const a = fieldVal.trim().toLowerCase();
    const b = fullName.trim().toLowerCase();
    return a === b || b.startsWith(a + ' ') || a.startsWith(b + ' ') || b.includes(a) || a.includes(b);
  };

  const empContentWork = empName ? contents
    .filter(c => {
      const nameMatch = CONTENT_ROLE_FIELDS.some(f => nameMatches(c[f], empName));
      // castingList may be a JSON string (SQLite) or already parsed array
      let castingArr = [];
      try {
        castingArr = typeof c.castingList === 'string' ? JSON.parse(c.castingList) : (c.castingList || []);
      } catch (e) { castingArr = []; }
      const castingMatch = Array.isArray(castingArr) && castingArr.some(
        cast => nameMatches(typeof cast === 'string' ? cast : cast?.name || '', empName)
      );
      if (!nameMatch && !castingMatch) return false;
      // Directors/Cinematographers/Actors: Shoot Completed, Editing Completed, Released
      // Editors only: Editing Completed, Released
      const isEditorOnly = nameMatches(c.editor, empName) &&
        !['director', 'cinematographer', 'writer'].some(f => nameMatches(c[f], empName)) &&
        !castingMatch;
      if (isEditorOnly) return ['Editing Completed', 'Released'].includes(c.status);
      return true;
    })
    .map(c => {
      let castingArr = [];
      try {
        castingArr = typeof c.castingList === 'string' ? JSON.parse(c.castingList) : (c.castingList || []);
      } catch (e) { castingArr = []; }
      const roles = CONTENT_ROLE_FIELDS
        .filter(f => nameMatches(c[f], empName))
        .map(f => f.charAt(0).toUpperCase() + f.slice(1));
      const castingRole = Array.isArray(castingArr) && castingArr.some(
        cast => nameMatches(typeof cast === 'string' ? cast : cast?.name || '', empName)
      ) ? ['Actor'] : [];
      return {
        id: `content_${c.id}`,
        shootDate: c.shootDate,
        content: c.contentTitle || c.title || '-',
        myRole: [...roles, ...castingRole].join(', ') || 'Crew',
        contentType: c.contentType || 'Long Video',
        hours: null,
        comments: c.remarks || null,
        source: 'content_library',
        contentId: c.contentId,
      };
    }) : [];

  const empWork = [...empContentWork];
  const empTotalLeaveDays = empLeaves.reduce((s, l) => s + (parseFloat(l.numberOfDays) || 0), 0);
  const empTotalPermHours = empPermissions.reduce((s, p) => s + (parseFloat(p.hours) || 0), 0);
  const empCompOffDays = overviewCompOff.filter(c => c.status === 'approved').reduce((s, c) => s + (c.isHalfDay ? 0.5 : 1), 0);
  const empPendingLeaves = empLeaves.filter(l => l.status === 'pending').length;
  const empPendingPerms = empPermissions.filter(p => p.status === 'pending').length;
  const periodLabel = filterType === 'daterange' && dateRangeStart && dateRangeEnd
    ? `${dateRangeStart} – ${dateRangeEnd}`
    : filterType === 'yearly'
      ? `Year ${selectedYear}`
      : new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight={700} sx={{ display: { xs: 'none', sm: 'block' } }}>Manager Dashboard</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Filter by</InputLabel>
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Filter by">
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="daterange">Date Range</MenuItem>
            </Select>
          </FormControl>
          {filterType === 'monthly' ? (
            <FormControl size="small" sx={{ minWidth: { xs: 'auto', sm: 200 }, flexGrow: { xs: 1, sm: 0 } }}>
              <InputLabel>Month</InputLabel>
              <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} label="Month">
                {generateMonthOptions().map(o => <MenuItem key={o.value} value={o.value}>{o.label}{o.isCurrent ? ' (Current)' : ''}</MenuItem>)}
              </Select>
            </FormControl>
          ) : filterType === 'yearly' ? (
            <FormControl size="small" sx={{ minWidth: { xs: 'auto', sm: 200 }, flexGrow: { xs: 1, sm: 0 } }}>
              <InputLabel>Year</InputLabel>
              <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                {generateYearOptions().map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <>
              <TextField size="small" type="date" label="From" value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
              <TextField size="small" type="date" label="To" value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
            </>
          )}
          <Button variant="contained" startIcon={<Download />} onClick={exportToExcel} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Export</Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { color: 'primary.main', icon: <CalendarToday sx={{ fontSize: { xs: 32, md: 40 } }} />, value: filteredTotalDays.toFixed(1), label: 'Leave Days' },
          { color: 'info.main', icon: <AccessTime sx={{ fontSize: { xs: 32, md: 40 } }} />, value: filteredTotalHours.toFixed(1), label: 'Permission Hours' },
          { color: 'success.main', icon: <Work sx={{ fontSize: { xs: 32, md: 40 } }} />, value: stats.totalWorkLogs, label: 'Work Logs' },
          { color: 'secondary.main', icon: <People sx={{ fontSize: { xs: 32, md: 40 } }} />, value: stats.activeEmployees, label: 'Active Employees' },
        ].map((card, i) => (
          <Grid item xs={6} sm={6} md={3} key={i}>
            <Card sx={{ bgcolor: card.color, color: 'white' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  {card.icon}
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
                    <Typography variant="body2">{card.label}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Card */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(e, v) => { setActiveTab(v); setSearchQuery(''); }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Employee Overview" icon={<Person fontSize="small" />} iconPosition="start" />
          <Tab label={`Leaves (${filteredTotalDays.toFixed(1)} days)`} />
          <Tab label={`Permissions (${filteredTotalHours.toFixed(1)} hrs)`} />
          <Tab label={`Manual Punch (${manualPunches.filter(p => p.status === 'pending').length})`} />
          <Tab label="Comp-Off Approvals" />
          <Tab label="Content Approval" />
        </Tabs>

        {(activeTab === 1 || activeTab === 2) && (
          <Box p={2} display="flex" gap={2} bgcolor="grey.50" flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search by employee name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flexGrow: 1, bgcolor: 'white' }}
            />
            {searchQuery && activeTab === 0 && filteredLeaves.length > 0 && (
              <Chip label={`Total: ${filteredTotalDays.toFixed(1)} days`} color="primary" variant="outlined" />
            )}
            {searchQuery && activeTab === 1 && filteredPermissions.length > 0 && (
              <Chip label={`Total: ${filteredTotalHours.toFixed(1)} hours`} color="info" variant="outlined" />
            )}
          </Box>
        )}

        <CardContent>
          {activeTab === 0 && (
            <EmployeeOverviewTab
              employees={employees}
              overviewEmployeeId={overviewEmployeeId}
              setOverviewEmployeeId={setOverviewEmployeeId}
              selectedEmployee={selectedEmployee}
              empLeaves={empLeaves}
              empPermissions={empPermissions}
              empWork={empWork}
              overviewCompOff={overviewCompOff}
              overviewLoading={overviewLoading}
              empTotalLeaveDays={empTotalLeaveDays}
              empTotalPermHours={empTotalPermHours}
              empCompOffDays={empCompOffDays}
              empPendingLeaves={empPendingLeaves}
              empPendingPerms={empPendingPerms}
              periodLabel={periodLabel}
            />
          )}
          {activeTab === 1 && (
            <LeavesTable
              leaves={filteredLeaves}
              onEditLeave={handleEditLeave}
              onDeleteLeave={(id, name) => setDeleteDialog({ open: true, type: 'leave', id, employeeName: name })}
              onApproveLeave={handleApproveLeave}
              onRejectLeave={handleRejectLeave}
              userRole={user?.role}
            />
          )}
          {activeTab === 2 && (
            <PermissionsTable
              permissions={filteredPermissions}
              onEditPermission={handleEditPermission}
              onDeletePermission={(id, name) => setDeleteDialog({ open: true, type: 'permission', id, employeeName: name })}
              onApprovePermission={handleApprovePermission}
              onRejectPermission={handleRejectPermission}
              userRole={user?.role}
            />
          )}
          {activeTab === 3 && (
            <Box p={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Manual Punch Requests</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip label={`${manualPunches.filter(p => p.status === 'pending').length} pending`} color="warning" size="small" />
                  <Button variant="contained" size="small" startIcon={<Schedule />}
                    onClick={() => { setMpApplyError(''); setMpApplyDialog(true); }}>
                    Apply for Employee
                  </Button>
                </Box>
              </Box>
              {manualPunchLoading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : manualPunches.length === 0 ? (
                <Alert severity="info">No manual punch requests found.</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Employee</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>In Time</strong></TableCell>
                        <TableCell><strong>Out Time</strong></TableCell>
                        <TableCell><strong>Hours</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manualPunches.map(p => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.main', fontSize: '0.7rem' }}>
                                {p.employee?.fullName?.charAt(0) || '?'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{p.employee?.fullName || '—'}</Typography>
                                <Typography variant="caption" color="text.secondary">{p.employee?.employeeId || ''}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{fmtDate(p.punchDate)}</TableCell>
                          <TableCell>{p.inTime}</TableCell>
                          <TableCell>{p.outTime || '—'}</TableCell>
                          <TableCell>{p.hours ? `${p.hours} hrs` : '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Tooltip title={p.reason} arrow>
                              <span>{p.reason}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip label={p.status} size="small" color={statusColor(p.status)} />
                            {p.status === 'rejected' && p.rejectionReason && (
                              <Typography variant="caption" color="error" display="block">{p.rejectionReason}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5} justifyContent="center">
                              {p.status === 'pending' && (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton size="small" color="success" onClick={() => handleMpStatusUpdate(p.id, 'approved', p.employee?.fullName)}>
                                      <CheckCircle fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject">
                                    <IconButton size="small" color="error" onClick={() => handleMpStatusUpdate(p.id, 'rejected', p.employee?.fullName)}>
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Edit">
                                <IconButton size="small" color="primary" onClick={() => openMpEditDialog(p)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error"
                                  onClick={() => setMpDeleteDialog({ open: true, id: p.id, employeeName: p.employee?.fullName || '' })}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {activeTab === 4 && (
            <CompOffApprovals
              filterType={filterType}
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              dateRangeStart={dateRangeStart}
              dateRangeEnd={dateRangeEnd}
            />
          )}
          {activeTab === 5 && (
            <ContentApprovalTab />
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, id: null, employeeName: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Delete this {deleteDialog.type} for <strong>{deleteDialog.employeeName}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, id: null, employeeName: '' })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Leave Dialog */}
      <Dialog open={editLeaveDialog} onClose={() => setEditLeaveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Leave Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField select fullWidth label="Leave Type" value={leaveFormData.leaveType}
                onChange={(e) => setLeaveFormData({ ...leaveFormData, leaveType: e.target.value })}>
                {leaveTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="Start Date" value={leaveFormData.startDate}
                onChange={(e) => setLeaveFormData({ ...leaveFormData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="date" label="End Date" value={leaveFormData.endDate}
                onChange={(e) => setLeaveFormData({ ...leaveFormData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Number of Days" value={leaveFormData.numberOfDays}
                onChange={(e) => setLeaveFormData({ ...leaveFormData, numberOfDays: e.target.value })}
                inputProps={{ step: '0.5', min: '0.5' }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Reason" value={leaveFormData.reason}
                onChange={(e) => setLeaveFormData({ ...leaveFormData, reason: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditLeaveDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLeave}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permission Dialog */}
      <Dialog open={editPermissionDialog} onClose={() => setEditPermissionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Permission Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth type="date" label="Permission Date" value={permissionFormData.permissionDate}
                onChange={(e) => setPermissionFormData({ ...permissionFormData, permissionDate: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="time" label="Start Time" value={permissionFormData.startTime}
                onChange={(e) => setPermissionFormData({ ...permissionFormData, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth type="time" label="End Time" value={permissionFormData.endTime}
                onChange={(e) => setPermissionFormData({ ...permissionFormData, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Hours" value={permissionFormData.hours}
                onChange={(e) => setPermissionFormData({ ...permissionFormData, hours: e.target.value })}
                inputProps={{ step: '0.5', min: '0.5' }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Reason" value={permissionFormData.reason}
                onChange={(e) => setPermissionFormData({ ...permissionFormData, reason: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPermissionDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePermission}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Punch Edit Dialog */}
      <Dialog open={manualPunchDialog.open} onClose={() => setManualPunchDialog({ open: false, punch: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Manual Punch — {manualPunchDialog.punch?.employee?.fullName}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {manualPunchFormError && <Grid item xs={12}><Alert severity="error">{manualPunchFormError}</Alert></Grid>}
            <Grid item xs={12}>
              <TextField fullWidth required label="Date" type="date"
                value={manualPunchForm.punchDate}
                onChange={e => setManualPunchForm(f => ({ ...f, punchDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="In Time" type="time"
                value={manualPunchForm.inTime}
                onChange={e => {
                  const inTime = e.target.value;
                  const hours = calcMpHours(inTime, manualPunchForm.outTime);
                  setManualPunchForm(f => ({ ...f, inTime, hours }));
                }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Out Time" type="time"
                value={manualPunchForm.outTime}
                onChange={e => {
                  const outTime = e.target.value;
                  const hours = calcMpHours(manualPunchForm.inTime, outTime);
                  setManualPunchForm(f => ({ ...f, outTime, hours }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Optional" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Hours" InputProps={{ readOnly: true }}
                value={manualPunchForm.hours ? `${manualPunchForm.hours} hrs` : (manualPunchForm.inTime && manualPunchForm.outTime ? '⚠ Out before In' : '—')}
                helperText="Auto-calculated"
                sx={{ '& .MuiInputBase-input': { fontWeight: 600, color: manualPunchForm.hours ? 'success.main' : manualPunchForm.outTime ? 'error.main' : 'text.disabled' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline rows={3} label="Reason"
                value={manualPunchForm.reason}
                onChange={e => setManualPunchForm(f => ({ ...f, reason: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualPunchDialog({ open: false, punch: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleMpSave} disabled={manualPunchSubmitting}>
            {manualPunchSubmitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Punch Delete Confirm */}
      <Dialog open={mpDeleteDialog.open} onClose={() => setMpDeleteDialog({ open: false, id: null, employeeName: '' })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Delete manual punch request for <strong>{mpDeleteDialog.employeeName}</strong>? This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setMpDeleteDialog({ open: false, id: null, employeeName: '' })}>Cancel</Button>
          <Button onClick={confirmMpDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Punch — Apply for Employee Dialog */}
      <Dialog open={mpApplyDialog} onClose={() => setMpApplyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Manual Punch for Employee</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {mpApplyError && <Grid item xs={12}><Alert severity="error">{mpApplyError}</Alert></Grid>}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={mpApplyForm.userId}
                  onChange={e => setMpApplyForm(f => ({ ...f, userId: e.target.value }))}
                  label="Employee"
                >
                  {employees
                    .filter(e => e.isActive !== false)
                    .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
                    .map(e => (
                      <MenuItem key={e.id} value={e.id}>
                        {e.fullName} {e.employeeId ? `(${e.employeeId})` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Date" type="date"
                value={mpApplyForm.punchDate}
                onChange={e => setMpApplyForm(f => ({ ...f, punchDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required label="In Time" type="time"
                value={mpApplyForm.inTime}
                onChange={e => {
                  const inTime = e.target.value;
                  const hours = calcMpHours(inTime, mpApplyForm.outTime);
                  setMpApplyForm(f => ({ ...f, inTime, hours }));
                }}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Out Time" type="time"
                value={mpApplyForm.outTime}
                onChange={e => {
                  const outTime = e.target.value;
                  const hours = calcMpHours(mpApplyForm.inTime, outTime);
                  setMpApplyForm(f => ({ ...f, outTime, hours }));
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Optional" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Hours" InputProps={{ readOnly: true }}
                value={mpApplyForm.hours ? `${mpApplyForm.hours} hrs` : (mpApplyForm.inTime && mpApplyForm.outTime ? '⚠ Out before In' : '—')}
                helperText="Auto-calculated"
                sx={{ '& .MuiInputBase-input': { fontWeight: 600, color: mpApplyForm.hours ? 'success.main' : mpApplyForm.outTime ? 'error.main' : 'text.disabled' } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline rows={3} label="Reason"
                placeholder="Reason for manual punch..."
                value={mpApplyForm.reason}
                onChange={e => setMpApplyForm(f => ({ ...f, reason: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMpApplyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMpApplySubmit} disabled={mpApplySubmitting}>
            {mpApplySubmitting ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar.message}
      />
    </Box>
  );
}

export default ManagerDashboard;
